import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'

export default function Accordion() {

  const versions = [
    {
      version_title: 'Version 1.9 - September 11, 2025',
      subtitle: 'Here\'s what\'s new in Version 1.9 ✨:',
      list: [
        {
          title: 'Zachomatic Import Tool REVAMP: Card-Style UI',
          content: 'Completely redesigned the import tool interface with a modern card-style layout. Each step is now isolated in its own card for less clutter and improved focus, making the import process more intuitive and user-friendly.'
        },
        {
          title: 'Temporary Emails for Invalid Contacts',
          content: 'Users can now generate temporary emails for invalid contacts with a click of a button. This turns invalid contacts to valid ones.'
        },
        {
          title: 'Upload Lead List and Project List Files Without Email Columns',
          content: 'Users can now upload Lead List and Project List files that don\'t have email columns. With the new toggle option to generate temporary emails, previously unusable files become fully functional for import.'
        },
        {
          title: 'Check for Name Collisions Feature',
          content: 'Added a comprehensive name collision detection system that searches HubSpot for existing contacts with matching names before import. Users can choose between full name or separate first/last name matching, with a modal interface showing collision results and resolution options.'
        },
        {
          title: 'Enhanced Deal Collision Information',
          content: 'Deal collision resolution now displays detailed contact information, showing how many contacts are currently associated with existing deals and how many contacts are incoming for each deal, providing better context for collision resolution decisions.'
        },
        {
          title: 'Download Invalid + Duplicate Emails CSV',
          content: 'Users can now download a separate CSV file containing all invalid and duplicate email contacts for review and manual correction.'
        },
        {
          title: 'Google Drive Upload for Invalid Contacts Removed',
          content: 'Removed the Google Drive upload functionality for invalid contacts since users can now download the invalid contacts CSV both before and after import, making the Google Drive integration unnecessary.'
        },
        {
          title: '"All Columns" Filter Option',
          content: 'Enhanced filtering capabilities with an "All Columns" option that allows users to search and filter across all mapped data columns simultaneously.'
        },
        {
          title: 'Enhanced Mapping GUI with Visual Indicators',
          content: 'Improved the column mapping interface with visual conflict detection. When multiple required columns are mapped to the same detected header, the mapping dropdowns turn red and show tooltips indicating the conflict. Recommended matches are marked with checkmarks for easier identification.'
        },
        {
          title: 'Guide Buttons for Better Navigation',
          content: 'Added contextual help buttons (?) throughout the interface that open detailed guide modals explaining each feature. These include step-by-step instructions, tips, and Loom video explanations for every step of the import process, making complex workflows easy to understand and follow.'
        },
        {
          title: 'Quantity Display for Filter Tags',
          content: 'Filter tags now display the quantity of matching records in parentheses (e.g., "Company (15)") when multiple values are selected, or show the actual value when only one is selected. This provides immediate visibility into filter impact and makes tag management more intuitive.'
        },
        {
          title: 'Import Button Safety Controls',
          content: 'The "Import File to TWS" button now automatically disables when there are any issues or conflicts detected, ensuring safe imports and preventing users from accidentally importing problematic data that could cause errors or data integrity issues.'
        }
      ]
    },
    {
      version_title: 'Version 1.8 - July 15, 2025',
      subtitle: 'Here\'s what\'s new in Version 1.8 ✨:',
      list: [
        {
          title: 'Adjusted Leads Counter to Use Pre-Count',
          content: 'The system now uses a pre-count method to calculate the number of leads prior to import, instead of querying HubSpot directly. This improves performance and reliability of lead import metrics.'
        },
        {
          title: 'Project ID Collision Management GUI',
          content: 'A new graphical interface allows users to review and resolve project ID collisions during imports more intuitively.'
        },
        {
          title: '"Create New" and "Use Existing" Collision Resolution Options',
          content: 'Users can now choose between creating a new deal or using an existing one when a project ID conflict is detected during import.'
        },
        {
          title: 'Exclude Button for Specific Collisions',
          content: 'An "Exclude" button has been added to the collision resolution interface, enabling users to selectively omit conflicting deals from being imported.'
        },
        {
          title: 'Proceed with Non-Collision Deals',
          content: 'A "Proceed" function now allows users to continue importing all non-colliding deals while collisions are handled separately.'
        },
        {
          title: 'File Upload Switch Warning Modal',
          content: 'A modal now alerts users when switching import types with a file already uploaded, preventing accidental data loss and bugs.'
        },
        {
          title: 'Enhanced Filter Tagging',
          content: 'Filters now apply tags instead of altering the file directly, making included/excluded matching ZIPs visible and editable.'
        },
      ]
    },
    {
      version_title: 'Version 1.7 - July 4, 2025',
      subtitle: 'Here\'s what\'s new in Version 1.7 ✨:',
      list: [
        {
          title: 'Complete Integration with Hubspot Enterprise',
          content: 'Import Leads from different sources, asign deals to enterprise users, adjusted deal info such as job site zip code, make sure locations are not overwritten.'
        },
        {
          title: 'Support for non-enterprise clients',
          content: ' Zachomatic now works with both free and Enterprise HubSpot accounts.'
        },
        {
          title: 'Enhanced lead-count analysis',
          content: 'Lead reports now incorporate data imported from legacy HubSpot accounts.'
        },
        {
          title: 'New isEnterprise flag',
          content: 'Added an isEnterprise column to the Clients table to identify which clients use HubSpot Enterprise.'
        },
        {
          title: 'Client page UI refresh',
          content: 'Streamlined the table row layout and introduced intuitive icons for editing and creating clients.'
        },
      ]
    },
    {
      version_title: 'Version 1.6 - May 1, 2025',
      subtitle: 'Here\'s what\'s new in Version 1.6 ✨:',
      list: [
        {
          title: 'Non-ConstructConnect/PlanHub Support for Project and Lead List uploads',
          content: 'Projects and leads outside of ConstructConnect and PlanHub can now be uploaded as deals and associated with contacts and companies in HubSpot.'
        },
        {
          title: 'Custom Deal Naming for Lead List Uploads',
          content: 'All leads from Lead List uploads are now grouped under a custom Project/Deal Name for better organization.'
        },
        {
          title: 'Auto Header Mapping for Project and Lead List',
          content: 'Headers are now automatically mapped to HubSpot fields using predefined synonyms like Family Name → Last Name.'
        },
        {
          title: 'Manual Header Mapping GUI for Project and Lead List',
          content: 'Users can manually adjust header mappings through a visual interface for better control and accuracy.'
        },
        {
          title: 'Contact Flattening and Project ID Option for Project and Lead List',
          content: 'Multiple contact fields (e.g., Contact Name 1, Contact Name 2) can now be flattened into a single Contact Name field, with an option to auto-generate missing Project IDs.'
        },
        {
          title: 'Email Validation Column Selection for Lead List',
          content: 'The email column is now automatically detected or manually selectable during Lead List uploads to ensure proper validation.'
        },
        {
          title: 'Multi-Sheet Support for Email Selection in Lead List',
          content: 'Users can now choose which sheet to validate contacts from when uploading multi-sheet files.'
        },
        {
          title: 'Multi-Select Filter Dropdown',
          content: 'The filter dropdown now supports multi-select and "Select All" options for easier and faster bulk filtering.'
        },
        {
          title: 'Total Leads Imported/Updated Counter',
          content: 'A pop-up notification now shows the total number of leads imported and updated after each upload to HubSpot which is also recorded in the database.'
        },
        {
          title: 'Feature Usage Tracking and Analytics',
          content: 'Admins can now track and analyze the usage of each feature in the system, including the number of leads imported, updated, and the time taken for each upload. Additionally, graphs are now available for better visualization of feature usage for each user with a date range filter.'
        },
        {
          title: 'Bug Fixes and Improvements',
          content: 'Fixed a bug where the feature usage graph was not showing the correct data and improved the overall user experience of the feature usage page. Handle expired sessions by redirecting and blocking private routes to prevent page lagging in Zachomatic.'
        },
        {
          title: 'Call Recording Duration Configuration',
          content: 'Added a feature to configure the call recording duration in the TWS Franchisee page'
        }

      ]
    },    
    {
      version_title: 'Version 1.5 - March 11, 2025',
      subtitle: 'Here\'s what\'s new in Version 1.5 ✨:',
      list: [
        {
          title: 'Client selection and pipeline stage configuration are now added to the Clients page',
          content: 'Admins can assign a franchisee to a client from the client list and optionally configure pipeline stages through the modal form.'
        },
        {
          title: 'Files from PlanHub can now be uploaded as deals',
          content: 'Projects from PlanHub can now be uploaded as deals which are also automatically associated to their respective contacts and companies.'
        },
        {
          title: 'Filtered File now uses DataTables for better user experience',
          content: 'The filtered file now uses DataTables for better user experience, including pagination, search, and sorting.'
        },
        {
          title: 'Whitelist and Blacklist filters now available for filtering',
          content: 'Users can now blacklist or whitelist any cells according to their column headers.'
        },
        {
          title: 'Whitelist and Blacklist input filters now contains all unique values',
          content: 'The input for Whitelist and Blacklist filters now autofills based on existing unique values in the selected column.'
        },
        {
          title: 'Non-TWS Clients can now be added to the system via the Clients Page',
          content: 'Admins can now add non-TWS clients to the system and assign them to an Agent.'
        }
      ]
    },
    {
      version_title: 'Version 1.4 - February 11, 2025',
      subtitle: 'Here\'s what\'s new in Version 1.4 ✨:',
      list: [
        {
          title: 'User Roles are now edittable in the Users Page',
          content: 'Admins can now edit user roles via a dropdown menu in the Users page, allowing for easy user management.'
        },
        {
          title: 'Preselect dropdowns in Users Page based on current values',
          content: 'Clicking on the dropdowns in the Users page will now preselect the current value.'
        },
        {
          title: 'Recording Channel ID now in TWS Franchisee page in table and modal',
          content: 'Admins can now add Recording Channel IDs to TWS Franchisees, which will be displayed in the table and create/edit modal.'
        },
        {
          title: 'Toggle Button for sending recordings to respective TWS Channels in TWS Franchisee modal',
          content: 'Admins can now choose to toggle sending RingCentral recordings via the Recording Channel ID.'
        },
        {
          title: 'EOD Backup Page now available for Admins and Agents!',
          content: 'Admins and Agents can now view the backuped EOD sheets content directly in Zach-O-Matic'
        },
        {
          title: 'EOD Backup Page now supports pagination',
          content: 'The EOD Backup Page now supports pagination for easier navigation and viewing of EOD sheets.'
        },
        {
          title: 'EOD Backup Page now has a SDR/Date search filter',
          content: 'Admins and Agents can now search for specific SDRs/Dates using the search filter in the EOD Backup Page.'
        },
        {
          title: 'EOD Backup Page now has a toggle sort button',
          content: 'The EOD Backup Page now has a toggle sort button for sorting EODs by ascending or descending order.'
        },
        {
          title: 'EOD Backup Page now has an items per page dropdown',
          content: 'The EOD Backup Page now has an items per page dropdown for selecting the number of EODs to display per page.'
        },
        // {
        //   title: 'Longitude and Latitude fields added to TWS Franchisee page',
        //   content: 'Admins now have the ability to add Longitude and Latitude coordinates to TWS Franchisees to include distances to hubspot.'
        // },
      ]
    },
    {
      version_title: 'Version 1.3 - November 27, 2024',
      subtitle: 'Here\'s what\'s new in Version 1.3 ✨:',
      list: [
        {
          title: 'Search Filter on TWS Franchisee Page',
          content: 'The TWS Franchisee page now supports search functionality by Owner Name or TWS Name.'
        },
        {
          title: 'Create and Edit Modal for TWS Franchisee Page',
          content: 'Admins can now create and edit TWS Franchisees using a validated modal form for seamless data management.'
        },
        {
          title: 'Fixed Associated TWS Dropdown on Users Page',
          content: 'The Associated TWS dropdown now functions correctly, including during scrolling and selection.'
        },
        {
          title: 'Soft Delete for TWS Franchisee',
          content: 'Admins now have the ability to soft delete a Franchisee, preserving data for future reference.'
        },
        {
          title: 'Restore Soft-Deleted Franchisees',
          content: 'Admins can now restore soft-deleted franchisees using the restore button available in the edit modal.'
        },
        {
          title: 'Checkbox to Toggle Soft-Deleted Franchisees',
          content: 'Admins now have the ability to view soft-deleted franchisees with distinct styling for easy identification.'
        },
        {
          title: 'Checkbox to Toggle Admins View in Users Page',
          content: 'A checkbox has been added to the Users page to allow toggling the view between Admins and Agents for better user management.'
        },
        {
          title: 'Alert Boxes for Form Submit Actions',
          content: 'Confirmation alert boxes have been added to all Create, Edit, Delete, and Restore actions across modal forms, ensuring clear user intent.'
        },
        {
          title: 'Null Option Added to Associated TWS Dropdown on Users Page',
          content: 'The Associated TWS dropdown on the Users page now includes a "Not Assigned" option, allowing admins to unassign a user from any TWS franchisee.'
        }        
      ]
    },
    {
      version_title: 'Version 1.2 - October 29, 2024',
      subtitle: 'Here\'s what\'s new in Version 1.2 ✨:',
      list: [
        {
          title:'Background Job Processing via Redis and Bull',
          content: 'Run resource intensive server processes on the background to avoid Heroku H12 errors (Request Timeout).'
        },
        {
          title: 'Store file assets in the AWS S3 Bucket for Cloud Storage',
          content: 'CSV files are uploaded to an S3 bucket to handle processing and cloud storage, preventing storage limitations on Heroku and ensuring file data persists across page refreshes.'
        },
        {
          title:'Company Watchlist CC Export Upload Option',
          content: 'Implemented an option to switch between company or project upload to HubSpot'
        },
        {
          title:'ClearDB to JawDB Migration',
          content: 'Moved entire database to JawDB since ClearDB will no longer be supported in November 2024 by Heroku.'
        },
        {
          title:'Merge Ringcentral Database with Zachomatic Database',
          content: 'The RingCentral to Slack Automation database has been integrated with the Zach-o-Matic database, allowing admins to view associated RingCentral information for registered users.'
        },
        {
          title: 'Assign or reassigned SDRs to TWS Franchisee',
          content: 'Admins can now easily assign SDRs to specific TWS franchisees and access their HubSpot accounts using the respective HubSpot API keys.'
        }
      ]
    },
    {
      version_title: 'Version 1.1 - October 12, 2024',
      subtitle: 'Here\'s what\'s new in Version 1.1 ✨:',
      list: [
        {
          title:'Automatic Excel File to CSV Conversion',
          content: 'Users can now upload both CSV and Excel files without the need for manual conversion.'
        },
        {
          title:'Database Integration',
          content: 'User credentials, along with their associated TWS franchisees and configuration settings, are now stored in the database.'
        },
        {
          title: 'Magic Link Authentication',
          content: 'Users can log in or sign up using magic links, enhancing both security and user experience. These links will be sent to the user\'s email and will remain valid for 30 minutes.'
        },
        {
          title: 'Login/Logout System',
          content: 'Users can log in, and their details will be retrieved from the database. Information stored in the website\'s local storage will be automatically cleared upon logout.'
        },
        {
          title: 'JWT Authentication',
          content: 'Unauthorized users without a valid JWT token will be restricted from accessing API requests. The JWT token will remain valid for 3 days, and users will need to re-authenticate via magic link once the token expires.'
        },
        {
          title: 'Role-Based Access Control',
          content: 'There are currently two user roles: Admin and Agent. Admins have access to all pages, including the lists of TWS franchisees and system users.',
          more: [
            'Admins can log in and switch between TWS franchisee configurations, allowing them to import leads to multiple HubSpot accounts associated with those franchisees.', 'Agents are limited to one TWS franchisee and can only upload leads to their designated TWS HubSpot account.'
          ]
        },
        {
          title: 'Zip Code Configuration & Other Filters',
          content: 'Users can set up zip code filter settings to exclude specific territories based on a provided list. Additional filters, such as project types and building uses, can also be configured.'
        },
        {
          title: 'Layout Improvements',
          content: 'A navigation bar has been added to facilitate easy page switching and logout. Some navigation links are visible only to admin users for security reasons'
        },
        {
          title: 'Configurable HubSpot API Key for TWS Franchisee',
          content: 'Admin users can now switch between different TWS accounts, allowing for automatic updates to their HubSpot API key configuration. This enables admins to upload without needing to sign in and out between accounts. The server API will access the HubSpot account of the currently selected TWS franchisee.'
        },
        {
          title: 'Added Release Notes Page',
          content: 'A release notes page has been added to help users track the latest technical features available in the Zachomatic HubSpot Import Tool.'
        },

      ]
    },
    {
      version_title: 'Version 1.0 - September 26, 2024',
      subtitle: 'Here\'s what\'s new in Version 1.0 ✨:',
      list: [
        {
          title: 'CSV File Upload',
          content: 'Users can now upload only CSV file formats. This means they must manually convert other formats (e.g., Excel) to CSV before uploading.'
        },
        {
          title: 'Import to HubSpot via API',
          content: 'Users can seamlessly upload contacts and projects from Construct Connect to HubSpot through the import API, eliminating the need for manual column mapping'
        },
        {
          title: 'Project Creation, Contact & Company Association',
          content: 'A deal object will automatically be created, designated as ‘Upcoming Projects’ by default. This project will be linked to the corresponding contacts and their associated companies.'
        },
        {
          title: 'Filter Summary',
          content: 'After filtering the uploaded file, users will see a summary of the results that includes key information such as the number of valid contacts, invalid contacts, and duplicate contacts.'
        },
        {
          title: 'Progress Bar & Modal Confirmation',
          content: 'When a user initiates the import process, a loading screen featuring a spinning image of our valued team member, Zach, will appear. The progress bar will show real-time processing percentages from the server, enhancing the user experience. Upon successful import, a confirmation modal will be displayed.'
        },

      ]
    },
];

  return (
    <div className="flex-grow flex justify-center items-center my-6 p-4">
      <div className="mx-auto w-full max-w-7xl divide-y divide-white/5 rounded-xl bg-white/5">
        {
          versions && (
            <>
              {
                versions.map((ver, index) => (
                  <Disclosure key={index} as="div" className="p-6" defaultOpen={index=== 0 ? true: false}>
                      <DisclosureButton className="group flex w-full items-center justify-between">
                        <span className="text-start text-sm/6 font-medium text-white group-data-[hover]:text-white/80">
                          <p>{ver.version_title}</p>
                        </span>
                        <ChevronDownIcon className="size-5 fill-white/60 group-data-[hover]:fill-white/50 group-data-[open]:rotate-180" />
                      </DisclosureButton>

                      <DisclosurePanel className="mt-2 text-sm text-white">
                        <p className='underline'>{ver.subtitle}</p>
                        <br></br>
                        <ul className="list-disc ml-5">
                          {
                            ver.list.map((item,index) => (
                              <li key={index} className='font-normal leading-loose text-white/80'><span className='font-bold text-hs-orange'>{item.title}:</span> {item.content}</li>
                            ))
                          }
                         
                          
                        </ul>
                      </DisclosurePanel>
                </Disclosure>
                ))
                  
              }
            </>
          )
        }


      </div>
    </div>
  );
}
